import { VoidCallback } from '@deqo/foundation';
import { useCallback, useMemo } from 'react';
import { useObserver } from '../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { TeamLiteData } from '../../api/ApiTypes';
import { CommandDispatcher } from '../../concepts/commands/CommandDispatcher';
import { AppEvents } from '../AppEvents';
import { AppNavigator } from '../AppNavigator';
import { useResolve } from '../Container';
import { useAccountGuardCallback } from '../account/AccountHooks';
import { AccountModel } from '../account/AccountModel';
import { usePermissionCheck } from '../common/PermissionElements';
import { memberInvitePopup } from '../dashboard/members/MemberInvitePopup';
import { asyncOptionsPopupWith } from '../popups/OptionsPopup';
import { PopupManager } from '../popups/PopupManager';
import { createTeamLimitPopup } from '../workspace/CreateTeamLimitPopup';
import { createTeamPopup } from '../workspace/CreateTeamPopup';
import { folderCreate } from '../workspace/FolderCommands';
import { teamRenamePopup } from '../workspace/TeamRenamePopup';
import { workspaceRemoveMember } from '../workspace/WorkspaceCommands';

export type CreateTeamOptions = {
    autoOpen?: boolean,
    onInvoke?: VoidCallback,
};

const useCanUserInviteTo = (team: TeamLiteData) => {
    const account = useResolve(AccountModel);
    const admin = usePermissionCheck('ac_team:write');
    if (!team) return false;
    if (team.type !== 'team') return false;
    if (!admin && account.metadata.membersCanInviteUsers === false) return false;
    return true;
};

export const useOnCreateTeam = ({ autoOpen = false, onInvoke }: CreateTeamOptions = {}) => {

    const nav = useResolve(AppNavigator);
    const popups = useResolve(PopupManager);
    const account = useObserver(useResolve(AccountModel));
    const quantity = account.plan.quantity ?? 1;
    const metric = account.plan.features.quantityMetric;

    const canCreateWorkspace = !!account.metadata.membersCanCreateWorkspaces;
    const onCreateWorkspace = useAccountGuardCallback(async () => {
        onInvoke?.();

        const current = account.workspaces.filter(x => x.type === 'team').length;

        if (metric === 'workspace' && current >= quantity) {
            await popups.invoke(createTeamLimitPopup());
        }
        else {
            const { actioned, data: workspaceId } = await popups.invoke(createTeamPopup());
            if (actioned && workspaceId && autoOpen) {
                nav.go(`~/${workspaceId}`);
            }
        }

    }, [account, quantity, onInvoke]);

    return { canCreateWorkspace, onCreateWorkspace };
};

export const useOnTeamAddUserViaPopup = (team: TeamLiteData, onOpen?: VoidCallback) => {
    const popups = useResolve(PopupManager);
    const canTeamAddUser = useCanUserInviteTo(team);
    const onTeamAddUser = useAccountGuardCallback(() => {
        if (!team) return;
        onOpen?.();
        popups.invoke(memberInvitePopup(team));
    }, [team]);
    return { canTeamAddUser, onTeamAddUser };
};

export const useOnTeamRemoveUser = (userId: string, team: TeamLiteData, onOpen?: VoidCallback) => {

    const dispatch = useResolve(CommandDispatcher);
    const canTeamRemoveUser = useCanUserInviteTo(team);
    const onTeamRemoveUser = useCallback(() => {
        if (!team) return;
        onOpen?.();
        dispatch(workspaceRemoveMember, [userId, team.id]);
    }, [userId, team]);
    return { canTeamRemoveUser, onTeamRemoveUser };
};

export const onTeamCancelInvite = () => {

    const api = useResolve(ApiClient);
    const popups = useResolve(PopupManager);

    return useCallback(async (inviteId: string) => {

        const execute = () => api.exec(v2.admin.invites.delete({ inviteId }));
        const popup = asyncOptionsPopupWith(
            'Remove Member',
            'Please confirm you want to remove this member?',
            [
                ['Remove Member', 'negative', execute]
            ]
        );

        await popups.invoke(popup);

    }, []);

};

export const useOnTeamRename = (teamId: string, onOpen?: VoidCallback) => {
    const popups = useResolve(PopupManager);
    return useCallback(async () => {
        onOpen?.();
        await popups.invoke(teamRenamePopup(teamId));
    }, [teamId, onOpen]);
};

export const useOnTeamDelete = (teamId: string, onOpen?: VoidCallback) => {

    const api = useResolve(ApiClient);
    const events = useResolve(AppEvents);
    const popups = useResolve(PopupManager);

    const performDelete = useCallback(() => api.exec(v2.workspaces.de1ete({ workspaceId: teamId })), [teamId]);
    const actionPopup = useMemo(() => asyncOptionsPopupWith(
        'Delete Team',
        'Please confirm you want to delete this team?  ' +
        'Any boards will be automatically archived.',
        [
            ['Delete Team', 'negative', performDelete]
            // [false, 'Cancel', 'common', 'cancel'],
        ]
    ), []);

    return useCallback(async () => {
        onOpen?.();

        const { actioned, data } = await popups.invoke(actionPopup);
        if (!actioned || !data) return false;

        events.push('workspace:delete', { workspaceId: teamId });

    }, [teamId, actionPopup]);
};

export const useOnTeamCreateFolder = (teamId: string, onOpen?: VoidCallback) => {
    const dispatch = useResolve(CommandDispatcher);
    return useCallback(async () => {
        onOpen?.();
        await dispatch(folderCreate, [teamId]);
    }, [teamId, onOpen]);
};
